import axios from 'axios';
import { toast } from 'sonner';

export function useArchiveLogic({ API, getAuthHeader, fetchData, setArchiving, setGeneratingRecu, setSelectedDocument, setSelectedPaiement, setSelectedPaiementIndex, setShowRecuEcheanceModal, calculateMontantTTC }) {

  // Archiver un reçu dans la GED
  const archiveRecuToGED = async (souscriptionId, paiementId) => {
    setArchiving(true);
    try {
      await axios.post(
        `${API}/souscriptions/admin/${souscriptionId}/archive-recu`,
        { paiement_id: paiementId },
        { headers: getAuthHeader() }
      );
      
      toast.success('Reçu archivé dans la GED');
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'archivage');
    } finally {
      setArchiving(false);
    }
  };
  
  // Archiver tous les reçus d'une souscription
  const archiveAllRecusToGED = async (souscriptionId) => {
    setArchiving(true);
    try {
      const response = await axios.post(
        `${API}/souscriptions/admin/${souscriptionId}/archive-all-recus`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(`${response.data.archived_count} reçu(s) archivé(s) dans la GED`);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'archivage');
    } finally {
      setArchiving(false);
    }
  };
  
  // Télécharger un reçu partiel
  const downloadPartialReceipt = async (souscriptionId, paiementId) => {
    const toastId = toast.loading('Telechargement en cours...');
    try {
      const response = await axios.get(
        `${API}/souscriptions/admin/${souscriptionId}/partial-payment/${paiementId}/receipt`,
        {
          headers: getAuthHeader(),
          responseType: 'blob',
          onDownloadProgress: (e) => {
            if (e.total) {
              const pct = Math.round((e.loaded / e.total) * 100);
              toast.loading(`Telechargement ${pct}%`, { id: toastId });
            }
          }
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recu_Partiel_${paiementId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      
      toast.success('Recu partiel telecharge', { id: toastId });
    } catch {
      toast.error('Erreur lors du telechargement du recu', { id: toastId });
    }
  };

  // Télécharger le reçu
  const downloadRecu = async (doc) => {
    const toastId = toast.loading('Téléchargement en cours...');
    try {
      const endpoint = `${API}/souscriptions/${doc.id}/download-recu`;
      
      const response = await axios.get(endpoint, {
        headers: getAuthHeader(),
        responseType: 'blob',
        onDownloadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100);
            toast.loading(`Téléchargement ${pct}%`, { id: toastId });
          }
        }
      });
      
      if (response.data.type && !response.data.type.includes('pdf')) {
        const text = await response.data.text();
        try {
          const error = JSON.parse(text);
          toast.error(error.detail || 'Reçu non disponible');
        } catch {
          toast.error('Reçu non disponible');
        }
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const isAvenant = doc.type_document === 'avenant' || doc.numero_police?.startsWith('AV-');
      link.download = `Recu_${isAvenant ? 'Avenant' : 'Souscription'}_${(doc.numero_police || doc.id).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Reçu téléchargé avec succès');
    } catch (error) {
      let errorMsg = 'Erreur lors du téléchargement du reçu';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch {}
      } else if (typeof error.response?.data?.detail === 'string') {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    }
  };

  // Ouvrir le modal d'édition de reçu d'échéancier
  const openRecuEcheanceModal = (doc, paiement, index) => {
    setSelectedDocument(doc);
    setSelectedPaiement(paiement);
    setSelectedPaiementIndex(index);
    setShowRecuEcheanceModal(true);
  };

  // Générer le reçu de paiement d'échéancier avec référence contribution
  const generateRecuEcheance = async (doc, paiement, index) => {
    setGeneratingRecu(true);
    try {
      const refContribution = doc.numero_police || doc.id;
      const numeroRecu = `RCS-ECH-${refContribution}-${String(index + 1).padStart(2, '0')}`;
      
      const response = await axios.post(
        `${API}/souscriptions/admin/${doc.id}/generate-recu-echeance`,
        {
          paiement_index: index,
          montant: paiement.amount || paiement.montant,
          date_paiement: paiement.date,
          mode_paiement: paiement.method || paiement.mode_paiement || 'virement',
          reference_paiement: paiement.reference || '',
          numero_recu: numeroRecu,
          reference_contribution: refContribution,
          est_paiement_final: paiement.est_solde || false
        },
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${numeroRecu}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reçu ${numeroRecu} généré avec succès`);
      setShowRecuEcheanceModal(false);
      fetchData(true);
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          await downloadPartialReceipt(doc.id, paiement._id || `paiement_${index}`);
        } catch {
          toast.error('Erreur lors de la génération du reçu');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de la génération du reçu');
      }
    } finally {
      setGeneratingRecu(false);
    }
  };

  // Générer le reçu de contribution acquittée (solde final)
  const generateRecuContributionAcquittee = async (doc) => {
    setGeneratingRecu(true);
    try {
      const refContribution = doc.numero_police || doc.id;
      const numeroRecu = `RCS-ACQ-${refContribution}`;
      
      const response = await axios.post(
        `${API}/souscriptions/admin/${doc.id}/generate-recu-acquitte`,
        {
          numero_recu: numeroRecu,
          reference_contribution: refContribution,
          montant_total: calculateMontantTTC(doc),
          date_acquittement: new Date().toISOString().split('T')[0],
          mention_acquittee: true
        },
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${numeroRecu}_ACQUITTE.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reçu de contribution acquittée ${numeroRecu} généré`);
    } catch (error) {
      if (error.response?.status === 404) {
        await downloadRecu(doc);
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de la génération du reçu');
      }
    } finally {
      setGeneratingRecu(false);
    }
  };

  // Visualiser le reçu dans un nouvel onglet
  const viewRecu = async (doc) => {
    try {
      const endpoint = `${API}/souscriptions/${doc.id}/download-recu`;
      
      const response = await axios.get(endpoint, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      if (response.data.type && !response.data.type.includes('pdf')) {
        const text = await response.data.text();
        try {
          const error = JSON.parse(text);
          toast.error(error.detail || 'Reçu non disponible');
        } catch {
          toast.error('Reçu non disponible');
        }
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('Reçu ouvert dans un nouvel onglet');
    } catch (error) {
      let errorMsg = 'Erreur lors de la visualisation du reçu';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch {}
      } else if (typeof error.response?.data?.detail === 'string') {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    }
  };

  return {
    archiveRecuToGED,
    archiveAllRecusToGED,
    downloadPartialReceipt,
    downloadRecu,
    openRecuEcheanceModal,
    generateRecuEcheance,
    generateRecuContributionAcquittee,
    viewRecu,
  };
}
